import React, { useState } from "react";
import {
  Box,
  Button,
  DialogContent,
  Grid,
  IconButton,
  Modal,
  TextField,
  InputLabel,
  Typography,
  MenuItem,
} from "@mui/material";
import {
  get,
  postJsonData,
  postMultipartData,
} from "../../network/ApiController";
import ApiEndpoints from "../../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../../utils/ToastUtil";
import ModalHeader from "../../modals/ModalHeader";
import ModalFooter from "../../modals/ModalFooter";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { whiteColor } from "../../theme/setThemeColor";
import Autocomplete from "@mui/material/Autocomplete";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40%",
  bgcolor: "background.paper",
  boxShadow: 24,
  fontFamily: "Poppins",
  p: 2,
  height: "max-content",
  overflowY: "scroll",
};

const CreateAssetModal = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    cost: "",
    category: "",
    purchase_date: "",
    purchase_from: "",
  });
  const [invoiceFiles, setInvoiceFiles] = useState([]); // updated
  const [loading, setLoading] = useState(false);
  const [developerId, setDeveloperId] = useState(null); // State to store selected developer ID
  const [developers, setDevelopers] = useState([]); // Store developers

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };
  const fetchDevelopers = async () => {
    if (developers.length === 0) {
      setLoading(true);
      try {
        postJsonData(
          `${ApiEndpoints.GET_USERS_NAME}?role=developer`, // API endpoint with query role=developer
          "",
          setLoading,
          (res) => {
            setDevelopers(res.data || []); // Set developers from API response
          },
          (error) => {
            apiErrorToast(error);
          }
        );
      } catch (error) {
        apiErrorToast("Failed to fetch developers");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 3) {
      apiErrorToast("You can upload up to 3 invoice files only.");
      return;
    }
    setInvoiceFiles(files);
  };

  const handleSubmit = () => {
    const payload = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      payload.append(key, value);
    });

    payload.append("assigned_to", developerId);

    // Append all files under the same key
    invoiceFiles.forEach((file) => {
      payload.append("invoices", file);
    });

    setLoading(true);

    postJsonData(
      ApiEndpoints.CREATE_ASSET,
      payload,
      setLoading,
      (response) => {
        if (response) {
          okSuccessToast(response.data.message);
          setOpen(false);
          if (onSuccess) onSuccess();
        } else {
          apiErrorToast(response.message || "Asset creation failed.");
        }
      },
      (error) => {
        apiErrorToast(error.message || "Error creating asset.");
      }
    );
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        startIcon={
          <IconButton sx={{ p: 0, color: whiteColor() }}>
            <AddCircleOutlineIcon />
          </IconButton>
        }
        size="small"
        color="success"
        variant="contained"
        sx={{
          padding: 1,
          backgroundColor: "#2F3B57",
          borderRadius: "2px",
          color: whiteColor(),
        }}
      >
        Create Asset
      </Button>
      <Box>
        <Modal
          open={open}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style} className="sm_modal">
            <ModalHeader
              title="Create New Asset"
              handleClose={() => setOpen(false)}
            />
            <DialogContent>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Asset Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Cost"
                    name="cost"
                    value={formData.cost}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Purchase From"
                    name="purchase_from"
                    value={formData.purchase_from}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    fullWidth
                    options={developers}
                    loading={loading}
                    onOpen={fetchDevelopers} // API call when opened
                    getOptionLabel={(option) => option.name || ""}
                    value={
                      developers.find((dev) => dev.id === developerId) || null
                    }
                    onChange={(event, newValue) => {
                      setDeveloperId(newValue ? newValue.id : null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assign Developer"
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? "Loading..." : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Purchase Date"
                    name="purchase_date"
                    type="date"
                    value={formData.purchase_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputLabel>Invoice Upload</InputLabel>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    multiple // allows selecting multiple files
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <ModalFooter
              btn="Create"
              request={loading}
              onClick={handleSubmit}
             
            />
          </Box>
        </Modal>
      </Box>
    </>
  );
};

export default CreateAssetModal;
